import React, { useState, useEffect } from "react"

export interface AddCashTicketsModalProps {
  open: boolean
  onClose: () => void
  /** Default price per ticket (from content_hash ticket_price_at_door || ticket_price). */
  defaultPricePerTicket: number
  onSubmit: (
    numTickets: number,
    totalCashAmount: number,
  ) => void | Promise<void>
  isSubmitting?: boolean
  ticketsRemaining?: number | null
  cashTicketsPurchased?: number | null
  maxTickets?: number | null
}

export const AddCashTicketsModal: React.FC<AddCashTicketsModalProps> = ({
  open,
  onClose,
  defaultPricePerTicket,
  onSubmit,
  isSubmitting = false,
  ticketsRemaining,
  cashTicketsPurchased,
  maxTickets,
}) => {
  const ticketsRemainingSubCash =
    ticketsRemaining != null && cashTicketsPurchased != null
      ? ticketsRemaining - cashTicketsPurchased
      : (ticketsRemaining ?? null)
  const [numTickets, setNumTickets] = useState<string>("1")
  const [totalCashAmount, setTotalCashAmount] = useState(defaultPricePerTicket)

  // Sync total when default price or num tickets changes (user can still override total manually)
  useEffect(() => {
    if (open) {
      setNumTickets("1")
      setTotalCashAmount(defaultPricePerTicket * 1)
    }
  }, [open, defaultPricePerTicket])

  const handleNumTicketsChange = (raw: string) => {
    setNumTickets(raw)
    const parsed = parseInt(raw, 10)
    if (!isNaN(parsed) && parsed >= 1) {
      setTotalCashAmount(defaultPricePerTicket * parsed)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const parsed = parseInt(numTickets, 10)
    onSubmit(Math.max(1, isNaN(parsed) ? 1 : parsed), totalCashAmount)
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-cash-tickets-modal-title"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h2
          id="add-cash-tickets-modal-title"
          className="text-xl font-bold text-gray-900 pb-2"
        >
          Add Cash Tickets
        </h2>
        {ticketsRemainingSubCash != null && ticketsRemainingSubCash < 10 && (
          <p className="text-red-600 italic text-xs pb-2">
            Only {ticketsRemainingSubCash} tickets remain of the original{" "}
            {maxTickets} tickets allotted
          </p>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="add-cash-num-tickets"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Number of Tickets
            </label>{" "}
            <input
              id="add-cash-num-tickets"
              type="number"
              min={1}
              step={1}
              value={numTickets}
              onChange={(e) => handleNumTicketsChange(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none text-sm"
            />
          </div>
          <div>
            <label
              htmlFor="add-cash-total-amount"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Total Cash Amount
            </label>
            <input
              id="add-cash-total-amount"
              type="number"
              min={0}
              step={1}
              value={totalCashAmount}
              onChange={(e) =>
                setTotalCashAmount(parseFloat(e.target.value) || 0)
              }
              className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none text-sm"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 focus:outline-none"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 focus:outline-none disabled:opacity-50"
            >
              {isSubmitting ? "Saving…" : "Add Cash Tickets"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
